import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { useState, useEffect } from "react";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { AddInvitedCredential } from "~/components/cco-claims/add-invited-credential";
import { ClaimStatusList } from "~/components/cco-claims/claim-status-list";
import { ClaimSuggestions } from "~/components/cco-claims/claim-suggestions";
import { CCOCombobox } from "~/components/cco-combobox";
import { ChallengeSelection } from "~/components/challenge-selection";
import { CTPSelection } from "~/components/ctp-selection";
import { ImageInput } from "~/components/file-input";
import {
  FormErrorSummary,
  notifyInvalidSubmit,
} from "~/components/form/form-error-summary";
import {
  FormCheckboxField,
  FormInputField,
} from "~/components/form/form-fields";
import { FrameworkSelection } from "~/components/framework-selection";
import {
  PristineInvalidAlert,
  usePristineInvalidAlert,
} from "~/components/pristine-invalid-alert";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { ErrorMessage, FormField } from "~/components/ui/form-field";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import { cn } from "~/lib/cn";
import {
  QualificationsSchema,
  getCoachQualifications,
  getQualificationPickerOptions,
  myClaimStatusesQueryOptions,
  saveQualifications,
} from "~/lib/functions/coach-qualifications";
import { parseCredentialParam } from "~/lib/shared/organization-invite";

type FormData = z.infer<typeof QualificationsSchema>;

export const Route = createFileRoute(
  "/_user/my-coaching/profile/qualifications",
)({
  staticData: { title: "Qualifications" },
  // `review=membership:<id>` or `program:<id>` opens "Ask DCM to review",
  // as the button in the "not confirmed" email does. `add=` in the same
  // shape offers a credential from an organization's invite link.
  validateSearch: z.object({
    review: z.string().optional(),
    add: z.string().optional(),
  }),
  loader: async () => {
    const { getGroupedFrameworksFn } =
      await import("~/lib/functions/cih/frameworks");
    const { getPublishedChallengesFn } =
      await import("~/lib/functions/cih/challenges");
    const [coachQualifications, pickerOptions, allFrameworks, allChallenges] =
      await Promise.all([
        getCoachQualifications(),
        getQualificationPickerOptions(),
        getGroupedFrameworksFn(),
        getPublishedChallengesFn(),
      ]);
    return {
      coachQualifications,
      allCcos: pickerOptions.ccos,
      allCtps: pickerOptions.ctps,
      allFrameworks,
      allChallenges,
    };
  },
  component: QualificationsPage,
});

function QualificationsPage() {
  const queryClient = useQueryClient();
  const {
    coachQualifications,
    allCcos,
    allCtps,
    allFrameworks,
    allChallenges,
  } = Route.useLoaderData();
  const { review, add } = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();

  const { register, handleSubmit, formState, control, reset, resetField } =
    useForm<FormData>({
      resolver: zodResolver(QualificationsSchema),
      defaultValues: coachQualifications,
    });

  const { errors, isDirty } = formState;

  const {
    fields: certifications,
    append: appendCertification,
    move: moveCertification,
  } = useFieldArray({
    control,
    name: "certifications",
    keyName: "fieldId",
  });

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => saveQualifications({ data }),
    onSuccess: async (freshData) => {
      toast.success("Qualifications saved");
      reset(freshData);
      await queryClient.invalidateQueries({ queryKey: ["coach"] });
    },
    onError: () => {
      toast.error("Failed to save qualifications");
    },
  });

  const { showExitAlert, onDiscard, onCancel } = useUnsavedChangesAlert(
    () => isDirty,
  );
  const pristineAlert = usePristineInvalidAlert(
    () => !isDirty && Object.keys(errors).length > 0,
  );

  const [ccoTypeDisplayName, setCcoTypeDisplayName] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const loadCcoDisplayNames = async () => {
      try {
        const { CCO_TYPE_DISPLAY_NAME } = await import("~/lib/ccos");
        setCcoTypeDisplayName(CCO_TYPE_DISPLAY_NAME);
      } catch (error) {
        console.error("Failed to load CCO display names:", error);
      }
    };
    loadCcoDisplayNames();
  }, []);

  const credentialToAdd = parseCredentialParam(add);
  const listsCredentialToAdd =
    credentialToAdd?.kind === "membership"
      ? coachQualifications.ccoIds.includes(credentialToAdd.targetId)
      : coachQualifications.ctps.some(
          (ctp) => ctp.ctpId === credentialToAdd?.targetId,
        );
  const dismissAdd = (): void => {
    void navigate({ search: (prev) => ({ ...prev, add: undefined }) });
  };
  // Reload the form after adding, so a later save cannot remove the claim.
  const reloadAfterAdding = async (): Promise<void> => {
    reset(await getCoachQualifications());
    await Promise.all([
      router.invalidate(),
      queryClient.invalidateQueries({
        queryKey: myClaimStatusesQueryOptions().queryKey,
      }),
    ]);
    dismissAdd();
  };

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  const addCertification = () => {
    appendCertification({
      _delete: false,
      id: undefined,
      title: "",
      issuer: "",
      imageSrc: null,
      url: null,
      order: certifications.length,
      tempId: `temp-${Date.now()}-${Math.random()}`,
    });
  };

  const handleMoveCertification = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= certifications.length) return;
    moveCertification(fromIndex, toIndex);
  };

  return (
    <>
      <UnsavedChangesAlert
        show={showExitAlert}
        onDiscard={onDiscard}
        onCancel={onCancel}
      />
      <PristineInvalidAlert {...pristineAlert} />
      <form
        onSubmit={handleSubmit(onSubmit, notifyInvalidSubmit)}
        className="space-y-8"
      >
        <ProfileEditHeader
          titleAs="h2"
          title="Qualifications"
          description="Add your certifications and experience."
          form={formState}
          isSaving={saveMutation.isPending}
          username={coachQualifications.username}
        />

        <FormErrorSummary
          errors={errors}
          labels={{
            ccoIds: "Coach certifying organizations",
            ctps: "Coaching training programs",
            frameworkIds: "Coaching frameworks",
            challengeIds: "Challenges you help with",
            "certifications.*.title": "Certification · title",
            "certifications.*.issuer": "Certification · issuer",
            "certifications.*.imageSrc": "Certification · image",
            "certifications.*.url": "Certification · URL",
          }}
        />

        <input
          {...register("id")}
          type="hidden"
          value={coachQualifications.id}
        />

        <ClaimSuggestions
          onAccepted={async () => {
            // The accepted claim is now saved; without this, the form's next
            // save would sync the old lists and delete it again.
            const fresh = await getCoachQualifications();
            resetField("ccoIds", { defaultValue: fresh.ccoIds });
            resetField("ctps", { defaultValue: fresh.ctps });
          }}
        />

        {credentialToAdd && !listsCredentialToAdd && (
          <AddInvitedCredential
            credential={credentialToAdd}
            blocked={isDirty}
            onAdded={reloadAfterAdding}
            onDismiss={dismissAdd}
          />
        )}

        <ClaimStatusList reviewClaimId={review} />

        <Card error={errors.ccoIds}>
          <CardHeader>
            <CardTitle>Coach certifying organizations</CardTitle>
            <CardDescription>
              Select the coaching organizations you're certified with or
              affiliated to.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Controller
              control={control}
              name="ccoIds"
              render={({ field }) => (
                <CCOCombobox
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                  groupedCCOs={allCcos}
                  ccoTypeDisplayName={ccoTypeDisplayName}
                />
              )}
            />
            {errors.ccoIds && <ErrorMessage error={errors.ccoIds.message} />}
          </CardContent>
        </Card>

        <Card error={errors.ctps}>
          <CardHeader>
            <CardTitle>Coaching training programs</CardTitle>
            <CardDescription>
              Select the coaching training programs you've completed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Controller
              control={control}
              name="ctps"
              render={({ field }) => (
                <CTPSelection
                  selectedCtps={field.value || []}
                  onSelectionChange={field.onChange}
                  groupedCTPs={allCtps}
                />
              )}
            />
            {errors.ctps && <ErrorMessage error={errors.ctps.message} />}
          </CardContent>
        </Card>

        <Card error={errors.frameworkIds}>
          <CardHeader>
            <CardTitle>Coaching frameworks</CardTitle>
            <CardDescription>
              Select the coaching frameworks and methodologies you practice.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Controller
              control={control}
              name="frameworkIds"
              render={({ field }) => (
                <FrameworkSelection
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                  groupedFrameworks={allFrameworks}
                />
              )}
            />
            {errors.frameworkIds && (
              <ErrorMessage error={errors.frameworkIds.message} />
            )}
          </CardContent>
        </Card>

        <Card error={errors.challengeIds}>
          <CardHeader>
            <CardTitle>Challenges you help with</CardTitle>
            <CardDescription>
              Tag the challenges you help clients work through. These appear
              both as tiles in the Challenges section of your profile and on the
              corresponding challenge pages, where prospective clients find
              coaches by topic.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Controller
              control={control}
              name="challengeIds"
              render={({ field }) => (
                <ChallengeSelection
                  selectedIds={field.value || []}
                  onSelectionChange={field.onChange}
                  challenges={allChallenges.map((c) => ({
                    id: c.id,
                    title: c.title,
                  }))}
                />
              )}
            />
            {errors.challengeIds && (
              <ErrorMessage error={errors.challengeIds.message} />
            )}
          </CardContent>
        </Card>

        <Card error={errors.certifications}>
          <CardHeader>
            <CardTitle>Certifications</CardTitle>
            <CardDescription>
              List any certifications relevant to your coaching practice.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button type="button" variant="primary" onClick={addCertification}>
              <Plus className="mr-2 h-4 w-4" /> Add certification
            </Button>

            <div className="space-y-4">
              {certifications.map((cert, index) => (
                <div
                  key={cert.fieldId}
                  className={cn(
                    "group w-full space-y-4 rounded-xl bg-gray-50 p-4",
                    {
                      "opacity-50": cert._delete,
                    },
                  )}
                >
                  {cert.id && (
                    <input
                      {...register(`certifications.${index}.id`)}
                      type="hidden"
                    />
                  )}
                  <div className="flex w-full items-center justify-between gap-2">
                    <FormCheckboxField
                      control={control}
                      name={`certifications.${index}._delete`}
                      label="Delete"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleMoveCertification(index, index - 1)
                        }
                        disabled={index === 0}
                      >
                        <ArrowUp className="size-[1em]" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleMoveCertification(index, index + 1)
                        }
                        disabled={index === (certifications?.length || 0) - 1}
                      >
                        <ArrowDown className="size-[1em]" />
                      </Button>
                    </div>
                  </div>
                  <FormInputField
                    control={control}
                    name={`certifications.${index}.title`}
                    label="Title"
                    required
                    helpText="The name of the certification"
                  />
                  <FormInputField
                    control={control}
                    name={`certifications.${index}.issuer`}
                    label="Issuer"
                    required
                    helpText="The organization that issued the certification"
                  />
                  <FormField
                    label="Image"
                    helpText="(32x32) The badge representing the issuer or certification"
                    error={errors.certifications?.[index]?.imageSrc?.message}
                  >
                    <ImageInput
                      {...register(`certifications.${index}.imageSrc`)}
                      value={cert.imageSrc}
                      purpose="certification"
                      type="url"
                      error={!!errors.certifications?.[index]?.imageSrc}
                    />
                  </FormField>
                  <FormInputField
                    control={control}
                    name={`certifications.${index}.url`}
                    label="URL"
                    helpText="Link to the organization or program"
                  />
                </div>
              ))}
            </div>
            {errors.certifications && (
              <ErrorMessage error={errors.certifications.message} />
            )}
          </CardContent>
        </Card>
      </form>
    </>
  );
}
